import { clsx } from "clsx";
import { format } from "date-fns";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";

import { SeverityIcon } from "@/features/schedule-notes/components/severity-icon";
import type { Note } from "@/features/schedule-notes/types.ts";
import {
	SEVERITIES,
	SEVERITY_LABELS,
} from "@/features/schedule-notes/utils/severity.ts";
import { isOpen } from "@/features/schedule-notes/utils/summary.ts";
import type { ConstructionSummary, ScheduleSpan } from "../../types.ts";
import type { ColorScheme } from "../../utils/colors.ts";
import type { DelayInfo } from "../../utils/delay.ts";
import {
	formatActualRange,
	formatDays,
	formatScheduleRange,
	formatScheduleTotal,
	ganttBar,
	scheduleDays,
} from "../../utils/gantt.ts";
import { buildPrintDomain, monthCells, weekCells } from "../../utils/scale.ts";
import { todayIndex } from "../../utils/timeline.ts";
import type { RowModel } from "../schedule-gantt/row.tsx";
import { GanttLegend } from "../schedule-gantt/legend.tsx";

function percentOf(fraction: number): string {
	return `${fraction * 100}%`;
}

export function SchedulePrint({
	construction,
	span,
	rows,
	scheme,
	topLevelNames,
	today,
	todayKey,
	notes,
}: {
	construction: ConstructionSummary;
	span: ScheduleSpan;
	rows: RowModel[];
	scheme: ColorScheme;
	topLevelNames: string[];
	today: Date;
	todayKey: string;
	notes: Note[];
}) {
	const openNotes = notes.filter(isOpen);
	const domain = buildPrintDomain(
		[
			span.start,
			span.end,
			construction.startDate,
			construction.expectedEndDate,
		],
		today,
	);
	const months = domain ? monthCells(domain) : [];
	const weeks = domain ? weekCells(domain, today) : [];
	const todayAt = domain ? todayIndex(domain, today) : null;
	const range = formatScheduleRange(span.start, span.end);
	const total = formatScheduleTotal(span.start, span.end);

	function delayLabel(delay: DelayInfo): string | null {
		if (delay.kind !== "delayed") return null;
		return `Atrasada ${formatDays(delay.days)}`;
	}

	return createPortal(
		<section className="schedule-print" data-testid="schedule-print">
			<table className="schedule-print__table">
				<caption className="schedule-print__caption">
					<span className="schedule-print__title">
						Cronograma · {construction.name}
					</span>
					<span className="schedule-print__meta">
						{range ? `Planejado: ${range} · ${total}` : "Sem datas planejadas."}
						{" · "}Impresso em {format(new Date(), "dd/MM/yyyy HH:mm:ss")}
					</span>
				</caption>
				<thead>
					<tr>
						<th className="schedule-print__col-name">Etapa</th>
						<th className="schedule-print__col-total">Total de dias</th>
						<th className="schedule-print__col-axis">
							{domain && (
								<div className="schedule-print__axis">
									<div className="schedule-print__months">
										{months.map((month) => (
											<span
												key={month.key}
												className="schedule-print__month"
												style={{
													left: percentOf(month.offsetDays / domain.totalDays),
													width: percentOf(month.days / domain.totalDays),
												}}
											>
												{month.label}
											</span>
										))}
									</div>
									<div className="schedule-print__weeks">
										{weeks.map((week) => (
											<span
												key={week.key}
												className={clsx(
													"schedule-print__week",
													week.today && "is-today",
												)}
												style={{
													left: percentOf(week.offsetDays / domain.totalDays),
													width: percentOf(week.days / domain.totalDays),
												}}
											>
												{week.label}
											</span>
										))}
									</div>
								</div>
							)}
						</th>
					</tr>
				</thead>
				<tbody>
					{rows.map(({ node, delay, percent, tone }) => {
						const planned = domain
							? ganttBar(domain, node.start, node.end)
							: null;
						const actual =
							domain && node.actualStart
								? ganttBar(domain, node.actualStart, node.actualEnd ?? todayKey)
								: null;
						const duration = scheduleDays(node.start, node.end);
						return (
							<tr key={node.id} className="schedule-print__row">
								<td
									className="schedule-print__name"
									style={{ paddingLeft: `${4 + node.depth * 10}px` }}
								>
									<span className="schedule-print__name-label">
										{node.name}
									</span>
									<span className="schedule-print__name-meta">
										{formatScheduleRange(node.start, node.end) ??
											"Sem cronograma"}
									</span>
									<span className="schedule-print__name-meta">
										{formatActualRange(node.actualStart, node.actualEnd)}
									</span>
								</td>
								<td className="schedule-print__total">
									{duration !== null ? formatDays(duration) : "—"}
									{delayLabel(delay) && (
										<span className="schedule-print__delay">
											{delayLabel(delay)}
										</span>
									)}
								</td>
								<td className="schedule-print__track">
									{domain && (
										<div className="schedule-print__lanes">
											{todayAt !== null && (
												<span
													className="schedule-print__today"
													style={{
														left: percentOf((todayAt + 0.5) / domain.totalDays),
													}}
												/>
											)}
											{planned && (
												<span
													className="schedule-print__bar is-planned"
													data-tone={tone}
													style={
														{
															left: percentOf(planned.leftFraction),
															width: percentOf(planned.widthFraction),
															"--progress": `${percent}%`,
														} as CSSProperties
													}
												>
													{duration !== null && (
														<span className="schedule-print__bar-label">
															{formatDays(duration)} · {percent}%
														</span>
													)}
												</span>
											)}
											{actual && (
												<span
													className="schedule-print__bar is-actual"
													data-open={node.actualEnd ? undefined : "true"}
													style={{
														left: percentOf(actual.leftFraction),
														width: percentOf(actual.widthFraction),
													}}
												/>
											)}
										</div>
									)}
								</td>
							</tr>
						);
					})}
				</tbody>
				<tfoot>
					<tr>
						<td colSpan={3}>
							<GanttLegend scheme={scheme} topLevelNames={topLevelNames} />
						</td>
					</tr>
				</tfoot>
			</table>
			{openNotes.length > 0 && (
				<section className="schedule-print__notes">
					<h2 className="schedule-print__notes-title">
						Alertas e observações abertas
					</h2>
					{SEVERITIES.map((severity) => {
						const group = openNotes.filter(
							(note) => note.severity === severity,
						);
						if (group.length === 0) return null;
						return (
							<div key={severity} className="schedule-print__notes-group">
								<h3
									className="schedule-print__notes-severity"
									data-severity={severity}
								>
									<SeverityIcon severity={severity} />
									{SEVERITY_LABELS[severity]} ({group.length})
								</h3>
								<ul className="schedule-print__notes-list">
									{group.map((note) => (
										<li key={note.id} className="schedule-print__note">
											<span className="schedule-print__note-stage">
												{note.stageName ?? "Obra"}
											</span>
											{note.body}
										</li>
									))}
								</ul>
							</div>
						);
					})}
				</section>
			)}
		</section>,
		document.body,
	);
}
